import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FileText, ArrowRight, IndianRupee, BookOpenCheck } from "lucide-react";
import { packagesApi } from "../../../../api/api";
import api from "../../../../utils/api";

const TestPackage = () => {
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get(`${packagesApi}`)
            .then((res) => {
                const dataArray = Array.isArray(res.data)
                    ? res.data
                    : res.data?.data || [];
                setPackages(dataArray);
            })
            .catch((err) => console.log(err))
            .finally(() => setLoading(false));
    }, []);

    const handleViewDetails = (id) => {
        window.location.href = `https://test.pyqpedia.com/Packages/singleproduct/${id}`;
    };

    const stripHtmlTags = (html) => {
        if (!html) return "";
        const temp = document.createElement("div");
        temp.innerHTML = html;
        return temp.textContent || temp.innerText || "";
    };

    const truncateDescription = (desc) => {
        const plainText = stripHtmlTags(desc);
        const words = plainText.split(" ");
        return words.length > 15
            ? words.slice(0, 15).join(" ") + "..."
            : plainText;
    };

    // Professional animation variants matching your theme
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 15 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.4, ease: "easeOut" },
        },
    };

    if (!loading && (!packages || packages.length === 0)) {
        return null; // Return null just like the original logic if no packages exist
    }

    return (
        <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gray-50 w-full border-t border-gray-200">
            <div className="max-w-7xl mx-auto">
                {/* Standardized Editorial Header */}
                <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6 border-b border-gray-200 pb-6">
                    <div className="max-w-2xl">
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5 }}
                        >
                            <div className="flex items-center gap-2 text-red-700 font-bold text-xs uppercase mb-3">
                                <span className="w-6 h-1 bg-red-700"></span>
                                Curated Test Series
                            </div>
                            <h2 className="text-4xl md:text-5xl font-black text-neutral-900 mb-4 ">
                                Test{" "}
                                <span className="text-yellow-500">
                                    Packages.
                                </span>
                            </h2>
                            <p className="text-gray-500 text-lg font-medium leading-relaxed">
                                Get exam-ready with our curated mock tests,
                                previous year questions, and comprehensive
                                evaluation modules.
                            </p>
                        </motion.div>
                    </div>
                </div>

                {loading ? (
                    /* Loading Skeleton */
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1, 2, 3].map((i) => (
                            <div
                                key={i}
                                className="bg-white rounded-xl border border-gray-200 shadow-sm animate-pulse h-72"
                            ></div>
                        ))}
                    </div>
                ) : (
                    /* Professional Grid */
                    <motion.div
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-50px" }}
                    >
                        {packages.map((item, index) => (
                            <motion.div
                                key={index}
                                variants={itemVariants}
                                className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-neutral-900 transition-all duration-300 flex flex-col group relative overflow-hidden"
                            >
                                {/* Subtle Hover Accent Line */}
                                <div className="absolute top-0 left-0 w-full h-[3px] bg-yellow-400 scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-500 ease-out z-10"></div>

                                <div className="p-6 flex flex-col flex-1">
                                    {/* Card Header */}
                                    <div className="flex justify-between items-start mb-5">
                                        <div className="w-12 h-12 bg-gray-50 group-hover:bg-yellow-400 text-neutral-900 rounded-lg flex items-center justify-center transition-colors duration-300 border border-gray-100 group-hover:border-yellow-400">
                                            {item.icon || (
                                                <FileText size={22} />
                                            )}
                                        </div>

                                        {/* Price Badge */}
                                        <div className="flex items-center px-3 py-1 bg-neutral-900 text-white rounded font-bold text-sm shadow-sm group-hover:bg-red-700 transition-colors duration-300">
                                            {item.amount === 0 ? (
                                                <span className="uppercase tracking-widest text-[11px]">
                                                    Free
                                                </span>
                                            ) : (
                                                <>
                                                    <IndianRupee
                                                        size={14}
                                                        className="mr-0.5"
                                                    />
                                                    {item.amount}
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <h3 className="text-xl font-black text-neutral-900 mb-3 group-hover:text-red-700 transition-colors duration-300 line-clamp-1">
                                        {item.name}
                                    </h3>

                                    <p className="text-gray-500 text-sm font-medium leading-relaxed line-clamp-3 mb-6 flex-1">
                                        {truncateDescription(
                                            item.description,
                                        ) ||
                                            "Comprehensive test modules designed to evaluate and improve your core competencies."}
                                    </p>

                                    {/* Button Group Row */}
                                    <div className="mt-auto pt-4 border-t border-gray-100 flex gap-3">
                                        <button
                                            onClick={() =>
                                                handleViewDetails(item.id)
                                            }
                                            className="flex-1 flex items-center justify-center px-4 py-2.5 bg-white border border-gray-200 hover:border-neutral-900 text-neutral-900 font-bold rounded-lg text-sm transition-all duration-300 group/btn"
                                        >
                                            <BookOpenCheck
                                                size={16}
                                                className="mr-2 text-gray-400 group-hover/btn:text-neutral-900 transition-colors"
                                            />
                                            Details
                                        </button>

                                        <a
                                            href={`https://test.pyqpedia.com/Login/index`}
                                            onClick={() =>
                                                sessionStorage.setItem(
                                                    "redirectFrom",
                                                    window.location.pathname,
                                                )
                                            }
                                            className="flex-1 block"
                                        >
                                            <button className="w-full flex items-center justify-center px-4 py-2.5 bg-neutral-900 hover:bg-yellow-400 hover:text-neutral-900 text-white font-bold rounded-lg text-sm transition-colors duration-300 group/enroll shadow-sm">
                                                Enroll
                                                <ArrowRight
                                                    size={16}
                                                    className="ml-2 transform group-hover/enroll:translate-x-1 transition-transform"
                                                />
                                            </button>
                                        </a>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                )}
            </div>
        </section>
    );
};

export default TestPackage;
